import { Fragment, type ReactNode } from 'react'

/**
 * A small Markdown reader for announcements, built as React elements — never
 * as HTML — so nothing an author types can run. It reads what announcements
 * need: headings (#, ##, ###), paragraphs, **bold**, *italics*, `code`,
 * links, images by URL, bulleted and numbered lists, quotes and rules. A
 * pasted address is a link on its own, and an image address alone on its
 * line is the picture. Links and images open only over http(s); anything
 * else stays as text.
 */
export function Markdown({ source, className }: { source: string; className?: string }) {
  return <div className={className}>{blocks(source)}</div>
}

const safeUrl = (url: string): string | null => (/^https?:\/\//i.test(url.trim()) ? url.trim() : null)

/** Inline marks, read left to right; the first mark to open wins. */
const INLINE = /!\[([^\]]*)\]\(([^)\s]+)\)|\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*\s][^*]*)\*|_([^_\s][^_]*)_|`([^`]+)`|(https?:\/\/[^\s<]*[^\s<.,;:!?)\]'"])/g

export function inline(text: string, key = 'i'): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let index = 0
  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0
    if (at > last) out.push(text.slice(last, at))
    const k = `${key}-${index++}`
    const [whole, imgAlt, imgUrl, linkText, linkUrl, bold1, bold2, em1, em2, code, bare] = match
    if (imgUrl !== undefined) {
      const src = safeUrl(imgUrl)
      out.push(src ? <img key={k} src={src} alt={imgAlt ?? ''} loading="lazy" className="my-3 max-h-[28rem] w-full rounded-xl object-contain" /> : whole)
    } else if (linkUrl !== undefined) {
      const href = safeUrl(linkUrl)
      out.push(href
        ? <a key={k} href={href} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-muted-foreground">{inline(linkText ?? '', k)}</a>
        : whole)
    } else if (bold1 !== undefined || bold2 !== undefined) {
      out.push(<strong key={k} className="font-semibold">{inline(bold1 ?? bold2 ?? '', k)}</strong>)
    } else if (em1 !== undefined || em2 !== undefined) {
      out.push(<em key={k}>{inline(em1 ?? em2 ?? '', k)}</em>)
    } else if (code !== undefined) {
      out.push(<code key={k} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.92em]">{code}</code>)
    } else if (bare !== undefined) {
      out.push(<a key={k} href={bare} target="_blank" rel="noopener noreferrer" className="break-all underline underline-offset-2 hover:text-muted-foreground">{bare}</a>)
    }
    last = at + whole.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Lines joined inside a paragraph keep their breaks, the way authors expect. */
const withBreaks = (lines: string[], key: string): ReactNode =>
  lines.map((line, i) => (
    <Fragment key={`${key}-${i}`}>
      {i > 0 ? <br /> : null}
      {inline(line, `${key}-${i}`)}
    </Fragment>
  ))

/** An image address standing alone on its line. */
export const IMAGE_LINE = /^\s*https?:\/\/\S+\.(png|jpe?g|gif|webp|avif)(\?\S*)?\s*$/i

function blocks(source: string): ReactNode[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const out: ReactNode[] = []
  let i = 0
  let n = 0
  while (i < lines.length) {
    const line = lines[i]!
    const key = `b${n++}`
    if (!line.trim()) { i++; continue }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line)
    if (heading) {
      const level = heading[1]!.length
      const content = inline(heading[2]!, key)
      out.push(level === 1
        ? <h2 key={key} className="mt-6 mb-2 text-[1.25rem] font-semibold text-foreground first:mt-0">{content}</h2>
        : level === 2
          ? <h3 key={key} className="mt-5 mb-2 text-[1.05rem] font-semibold text-foreground first:mt-0">{content}</h3>
          : <h4 key={key} className="mt-4 mb-1.5 text-ui font-semibold text-foreground first:mt-0">{content}</h4>)
      i++
      continue
    }
    if (IMAGE_LINE.test(line)) {
      out.push(<img key={key} src={line.trim()} alt="" loading="lazy" className="my-3 max-h-[28rem] w-full rounded-xl object-contain" />)
      i++
      continue
    }
    if (/^\s*(---|\*\*\*)\s*$/.test(line)) {
      out.push(<hr key={key} className="my-5 border-hairline" />)
      i++
      continue
    }
    if (/^\s*>/.test(line)) {
      const quoted: string[] = []
      while (i < lines.length && /^\s*>/.test(lines[i]!)) quoted.push(lines[i++]!.replace(/^\s*>\s?/, ''))
      out.push(<blockquote key={key} className="my-3 border-l-2 border-hairline pl-4 text-muted-foreground">{withBreaks(quoted, key)}</blockquote>)
      continue
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*[-*]\s+/, ''))
      out.push(<ul key={key} className="my-3 list-disc space-y-1 pl-5">{items.map((item, j) => <li key={j}>{inline(item, `${key}-${j}`)}</li>)}</ul>)
      continue
    }
    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+[.)]\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*\d+[.)]\s+/, ''))
      out.push(<ol key={key} className="my-3 list-decimal space-y-1 pl-5">{items.map((item, j) => <li key={j}>{inline(item, `${key}-${j}`)}</li>)}</ol>)
      continue
    }
    const paragraph: string[] = []
    while (
      i < lines.length && lines[i]!.trim() && !IMAGE_LINE.test(lines[i]!) &&
      !/^(#{1,3})\s|^\s*(---|\*\*\*)\s*$|^\s*>|^\s*[-*]\s+|^\s*\d+[.)]\s+/.test(lines[i]!)
    ) paragraph.push(lines[i++]!)
    out.push(<p key={key} className="my-3 first:mt-0 last:mb-0">{withBreaks(paragraph, key)}</p>)
  }
  return out
}
